import { get_req } from "../../Utilities/apiUtil";
import {
  delete_item_request_url,
  get_basket_request_url,
  update_item_request_url,
  get_desktop_orders_url,
} from "../../Utilities/apiUrl";
import { errorActions } from "../errorSlice";
import appConst from "../../Utilities/appConst.json";
import { basketActions } from "./basketSlice";
import {
  updateBasketItem,
  getPricingProducts,
  getShortPathUpgradePrice,
} from "./helpers";
import type { BasketApiResponse, BasketItemFull } from "./types";

// ── Fetch Basket ───────────────────────────────────────────────────────────────
// Used by: src/pages/Basket.jsx

export const getBasketData = (productType: string, isNewsletter: boolean) => {
  return async (dispatch: Function) => {
    try {
      let response: BasketApiResponse;
      let resolvedProductType = appConst.TILES;

      switch (productType) {
        case appConst.TILES:
          response = await get_req(get_basket_request_url);
          resolvedProductType = appConst.TILES;
          break;
        case appConst.BOOKS:
          response = await get_req(get_basket_request_url);
          resolvedProductType = appConst.BOOKS;
          break;
        case appConst.CALENDARS:
          response = await get_req(get_basket_request_url);
          resolvedProductType = appConst.CALENDARS;
          break;
        case appConst.ORDERS_UNION:
          response = await get_req(get_desktop_orders_url);
          resolvedProductType = appConst.ORDERS_UNION;
          break;
        default:
          response = await get_req(get_basket_request_url);
          resolvedProductType = appConst.TILES;
      }

      if (response && response.isValid && response.payload) {
        dispatch(
          basketActions.setBasketItems({
            payload: response.payload,
            product: resolvedProductType,
            isNewsletter: isNewsletter,
          })
        );
      } else {
        switch (response?.errorCode) {
          case appConst.ERROR_NO_ITEMS_IN_BASKET:
            dispatch(errorActions.setBasketEmpty(true));
            break;
          default:
            dispatch(errorActions.setError(true));
        }
      }
    } catch (error) {
      dispatch(errorActions.setError(true));
    }
  };
};

// ── Delete Items ───────────────────────────────────────────────────────────────
// Used by: src/pages/Basket.jsx

export const deleteBasketItems = (
  delItems: number[],
  product: string,
  isNewsletter: boolean
) => {
  return async (dispatch: Function) => {
    try {
      const res: BasketApiResponse = await get_req(
        `${delete_item_request_url}&item_ids=[${delItems}]`
      );

      if (!res.isValid) {
        switch (res.errorCode) {
          case appConst.ERROR_NO_ITEMS_IN_BASKET:
            dispatch(errorActions.setBasketEmpty(true));
            break;
          case 12:
            dispatch(errorActions.setError(true));
            break;
          default:
            dispatch(errorActions.setError(true));
            break;
        }
      } else {
        // Empty basket after deletion
        if (
          !res.payload ||
          !res.payload.items ||
          res.payload.items.length === 0
        ) {
          dispatch(errorActions.setBasketEmpty(true));
          return;
        }

        dispatch(
          basketActions.setBasketItems({
            payload: res.payload,
            product: product,
            isNewsletter: isNewsletter,
          })
        );
      }
    } catch (error) {
      dispatch(errorActions.setError(true));
    }
  };
};

// ── Update Item Size (TILES) ───────────────────────────────────────────────────
// Used by: src/Components/BasketItemTile.jsx

export const updateBasketItemSize = (
  newFormat: string | number,
  item: BasketItemFull
) => {
  return async (dispatch: Function, getState: Function) => {
    const existingItem: BasketItemFull | undefined = getState().basket.basketItems.find(
      (basketItem: BasketItemFull) => basketItem.item_id === item.item_id
    );

    if (!existingItem) {
      dispatch(errorActions.setError(true));
      return;
    }

    get_req(
      `${update_item_request_url}&format=${newFormat}&item_id=${existingItem.item_id}&quantity=${existingItem.quantity}&item_color=${existingItem.frame_color}&item_material=${existingItem.material}&is_basket_callback=false`
    )
      .then(() => {
        const nextItems = updateBasketItem(
          getState().basket.basketItems,
          item.item_id,
          { format: newFormat }
        );
        dispatch(basketActions.updateBasketItems(nextItems));
      })
      .catch(() => {
        dispatch(errorActions.setError(true));
      });
  };
};

// ── Update Item Format (BOOKS) ─────────────────────────────────────────────────
// Used by: src/Components/BasketItemBooks.jsx
// Note: function name has a typo ("Foramt") preserved for backward compatibility.

export const updateBasketItemForamt = (
  newFormat: string | number,
  item: BasketItemFull,
  isFromAlert: boolean = false
) => {
  return (dispatch: Function, getState: Function) => {
    const existingItem: BasketItemFull | undefined = getState().basket.basketItems.find(
      (basketItem: BasketItemFull) => basketItem.item_id === item.item_id
    );

    if (!existingItem) {
      dispatch(errorActions.setError(true));
      return;
    }

    get_req(
      `${update_item_request_url}&format=${newFormat}&item_id=${existingItem.item_id}&quantity=${existingItem.quantity}&cover=${existingItem.cover}&is_basket_callback=false&is_from_alert=${isFromAlert}&album_type=${existingItem.album_type}&current_format=${existingItem.format}`
    )
      .then(() => {
        const nextItems = updateBasketItem(
          getState().basket.basketItems,
          item.item_id,
          { format: newFormat }
        );
        dispatch(basketActions.updateBasketItems(nextItems));
      })
      .catch(() => {
        dispatch(errorActions.setError(true));
      });
  };
};

// ── Update Item Cover (BOOKS) ──────────────────────────────────────────────────
// Used by: src/Components/BasketItemBooks.jsx

export const updateBasketItemCover = (
  newCover: string | number,
  item: BasketItemFull
) => {
  return (dispatch: Function, getState: Function) => {
    const existingItem: BasketItemFull | undefined = getState().basket.basketItems.find(
      (basketItem: BasketItemFull) => basketItem.item_id === item.item_id
    );

    if (!existingItem) {
      dispatch(errorActions.setError(true));
      return;
    }

    get_req(
      `${update_item_request_url}&format=${existingItem.format}&item_id=${existingItem.item_id}&quantity=${existingItem.quantity}&cover=${newCover}&is_basket_callback=false`
    )
      .then(() => {
        const nextItems = updateBasketItem(
          getState().basket.basketItems,
          item.item_id,
          { cover: newCover }
        );
        dispatch(basketActions.updateBasketItems(nextItems));
      })
      .catch(() => {
        dispatch(errorActions.setError(true));
      });
  };
};

// ── Update Item Color (TILES) ──────────────────────────────────────────────────
// Used by: src/Components/BasketItemTile.jsx

export const updateBasketItemColor = (
  newColor: string | number,
  newUrl: string,
  item: BasketItemFull
) => {
  return (dispatch: Function, getState: Function) => {
    const existingItem: BasketItemFull | undefined = getState().basket.basketItems.find(
      (basketItem: BasketItemFull) => basketItem.item_id === item.item_id
    );

    if (!existingItem) {
      dispatch(errorActions.setError(true));
      return;
    }

    get_req(
      `${update_item_request_url}&format=${existingItem.format}&item_id=${
        existingItem.item_id
      }&quantity=${
        existingItem.quantity
      }&item_color=${newColor}&item_material=${
        newColor == 0 ? 1 : 2
      }&is_basket_callback=false`
    )
      .then(() => {
        const nextItems = updateBasketItem(
          getState().basket.basketItems,
          item.item_id,
          {
            frame_color: newColor,
            material:
              newColor == 0
                ? appConst.MATERIAL_KAPPA
                : appConst.MATERIAL_FRAME,
            itemImageUrl: newUrl,
          }
        );
        dispatch(basketActions.updateBasketItems(nextItems));
      })
      .catch(() => {
        dispatch(errorActions.setError(true));
      });
  };
};

// ── Update Item Quantity (all product types) ───────────────────────────────────
// Used by: src/hooks/useBasketItemHelpers.jsx, src/pages/Basket.jsx

export const updateBasketItemQuantity = (
  count: number,
  item: BasketItemFull
) => {
  return (dispatch: Function, getState: Function) => {
    const basketState = getState().basket;
    const existingItem: BasketItemFull | undefined = basketState.basketItems.find(
      (basketItem: BasketItemFull) => basketItem.item_id === item.item_id
    );

    if (!existingItem) {
      dispatch(errorActions.setError(true));
      return;
    }

    const urlParams = new URLSearchParams({
      format: String(existingItem.format),
      item_id: String(existingItem.item_id),
      quantity: String(count),
      is_basket_callback: "false",
    });

    // Different product types require different API params
    const configureRequest = (): boolean => {
      switch (basketState.productType) {
        case appConst.CALENDARS:
        case appConst.BOOKS:
        case appConst.ORDERS_UNION:
          urlParams.append("cover", String(existingItem.cover));
          return true;
        case appConst.TILES:
          urlParams.append("item_color", String(existingItem.frame_color));
          urlParams.append("item_material", String(existingItem.material));
          return true;
        default:
          dispatch(errorActions.setError(true));
          return false;
      }
    };

    if (!configureRequest()) {
      return;
    }

    const requestUrl = `${update_item_request_url}&${urlParams.toString()}`;

    get_req(requestUrl).catch(() => {
      dispatch(errorActions.setError(true));
    });
  };
};

// ── Batch Update All Items ─────────────────────────────────────────────────────
/** @deprecated Currently unused — no consumers import this thunk. */

export const updateBasketItems = () => {
  return (dispatch: Function, getState: Function) => {
    for (const item of getState().basket.basketItems) {
      get_req(
        `${update_item_request_url}&format=${item.format}&item_id=${item.item_id}&quantity=${item.quantity}&item_color=${item.frame_color}&item_material=${item.material}&is_basket_callback=false`
      ).catch(() => {
        dispatch(errorActions.setError(true));
      });
    }
  };
};

// ── Recalculate Short Path Upgrade Price ───────────────────────────────────────
/** @deprecated Currently unused — no consumers import this thunk. */

export const updateShortPathUpgradePrice = () => {
  return (dispatch: Function, getState: Function) => {
    const basketState = getState().basket;
    dispatch(
      basketActions.setShortPathUpgradePrice(
        getShortPathUpgradePrice({
          basketItems: basketState.basketItems,
          pricingProducts: getPricingProducts(basketState),
          productType: basketState.productType,
        })
      )
    );
  };
};
